import { useEffect, useMemo, useState } from "react";
import { TaskDisclosure } from "../components/TaskDisclosure";

function formatDateTime(value) {
  if (!value) {
    return "Not sent";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
}

function recipientLooksInternal(value = "") {
  return /(?:@dev\.rangegrid\.local|rangegrid-.*audit|owner-audit|test@|example\.com|\.example(?:\/|$))/i.test(String(value || ""));
}

function displayRecipient(value = "") {
  if (!value) {
    return "No email";
  }
  return recipientLooksInternal(value) ? "Owner review inbox" : value;
}

function displayMessageText(value = "") {
  return String(value || "")
    .replace(/^Owner review \d+$/i, "Owner review message")
    .replace(/^Owner review message \d+ for chapter readiness\.?$/i, "Chapter readiness review message");
}

function defaultDraft(ownerEmail = "") {
  return {
    segment: "test",
    chapterId: "",
    recipientEmail: recipientLooksInternal(ownerEmail) ? "" : ownerEmail,
    subject: "Range update",
    body: ""
  };
}

function ownerNotificationLabel(value) {
  if (!value) {
    return "Range message";
  }
  return String(value)
    .replace(/[._-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function segmentLabel(value) {
  const labels = {
    active_members: "Active members",
    pending_review: "Pending review",
    test: "Review recipient only",
    waiver_due: "Waiver due"
  };
  return labels[value] || ownerNotificationLabel(value);
}

function activityStatusLabel(notification = {}) {
  const status = notification.delivery?.status || notification.delivery?.mode || notification.status || "queued";
  const labels = {
    archived: "Archived",
    failed: "Delivery issue",
    outbox: "Waiting for review",
    queued: "Ready for review",
    reviewed: "Reviewed",
    sent: "Sent"
  };
  return labels[status] || ownerNotificationLabel(status);
}

function deliveryAttemptLabel(notification = {}) {
  const delivery = notification.delivery || {};
  const attempts = Number(delivery.attempts || 0);
  if (!attempts) {
    return "No delivery attempts";
  }
  const countLabel = `${attempts} attempt${attempts === 1 ? "" : "s"}`;
  if (delivery.status === "sent") {
    return `${countLabel} / sent`;
  }
  if (delivery.status === "failed") {
    return `${countLabel} / delivery issue`;
  }
  return countLabel;
}

function deliveryAttemptDetail(notification = {}) {
  const delivery = notification.delivery || {};
  if (delivery.lastError) {
    return ownerNotificationLabel(delivery.lastError);
  }
  if (delivery.senderEmail) {
    return `From ${delivery.senderEmail}`;
  }
  return "";
}

function notificationChapterId(notification) {
  return notification?.related?.chapterId || notification?.metadata?.chapterId || "";
}

function notificationChapterName(notification, chapterNameById) {
  return notification?.related?.chapterName ||
    notification?.metadata?.chapterName ||
    chapterNameById.get(notificationChapterId(notification)) ||
    "";
}

export function CommunicationsPage({
  chapters = [],
  chaptersEnabled = false,
  loading,
  members = [],
  notifications = [],
  notificationSummary,
  onPreviewAudience,
  onQueueNotification,
  onSendBroadcast,
  ownerEmail = "",
  queueing
}) {
  const [draft, setDraft] = useState(defaultDraft(ownerEmail));
  const [audiencePreview, setAudiencePreview] = useState(null);
  const [audiencePreviewLoading, setAudiencePreviewLoading] = useState(false);
  const [chapterFilter, setChapterFilter] = useState("");
  const [queuedMessage, setQueuedMessage] = useState("");
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  const activeChapters = useMemo(() => chapters.filter((chapter) => chapter.status !== "archived"), [chapters]);
  const chapterNameById = useMemo(() => new Map(activeChapters.map((chapter) => [chapter.chapterId, chapter.name])), [activeChapters]);
  const filteredNotifications = useMemo(() => (
    chapterFilter
      ? notifications.filter((notification) => notificationChapterId(notification) === chapterFilter)
      : notifications
  ), [chapterFilter, notifications]);
  const chapterNotificationCount = useMemo(() => (
    filteredNotifications.filter((notification) => notificationChapterId(notification)).length
  ), [filteredNotifications]);

  const segmentCount = useMemo(() => {
    const scopedMembers = draft.chapterId
      ? members.filter((member) => member.homeChapterId === draft.chapterId || (member.chapterIds || []).includes(draft.chapterId))
      : members;
    if (draft.segment === "active_members") {
      return scopedMembers.filter((member) => member.status === "active").length;
    }
    if (draft.segment === "pending_review") {
      return scopedMembers.filter((member) => member.review?.status === "pending" || member.status === "pending").length;
    }
    if (draft.segment === "waiver_due") {
      return scopedMembers.filter((member) => member.reviewSummary?.waiverStatus && member.reviewSummary.waiverStatus !== "completed").length;
    }
    return 1;
  }, [draft.chapterId, draft.segment, members]);
  const serverAudienceCount = audiencePreview?.eligibleCount ?? segmentCount;
  const skippedAudienceCount = audiencePreview?.skippedCount || 0;

  useEffect(() => {
    if (!onPreviewAudience) {
      setAudiencePreview(null);
      return undefined;
    }

    let cancelled = false;
    setAudiencePreviewLoading(true);
    onPreviewAudience({
      segment: draft.segment,
      chapterId: draft.chapterId
    }).then((preview) => {
      if (!cancelled) {
        setAudiencePreview(preview);
      }
    }).finally(() => {
      if (!cancelled) {
        setAudiencePreviewLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [draft.chapterId, draft.segment, onPreviewAudience]);

  async function queueReview(event) {
    event.preventDefault();
    const queued = await onQueueNotification({
      audience: "owner_test",
      segment: draft.segment,
      chapterId: draft.chapterId,
      chapterName: chapterNameById.get(draft.chapterId),
      recipientEmail: draft.recipientEmail,
      subject: draft.subject,
      body: draft.body
    });
    if (queued) {
      setQueuedMessage("Review message queued.");
    }
  }

  async function sendBroadcast() {
    if (!onSendBroadcast || draft.segment === "test") {
      return;
    }
    setSendingBroadcast(true);
    setQueuedMessage("");
    try {
      const result = await onSendBroadcast({
        segment: draft.segment,
        chapterId: draft.chapterId,
        chapterName: chapterNameById.get(draft.chapterId),
        subject: draft.subject,
        body: draft.body
      });
      if (result) {
        setQueuedMessage(`Broadcast sent to ${result.sent || 0} recipient${result.sent === 1 ? "" : "s"}.${result.failed ? ` ${result.failed} delivery issue${result.failed === 1 ? "" : "s"}.` : ""}`);
      }
    } finally {
      setSendingBroadcast(false);
    }
  }

  return (
    <div className="communications-page">
      {loading && <div className="status-strip">Loading communications...</div>}
      <section className="module-metrics">
        <div>
          <span>Needs review</span>
          <strong>{notificationSummary?.queued || 0}</strong>
        </div>
        <div>
          <span>Chapter activity</span>
          <strong>{chapterNotificationCount}</strong>
        </div>
        <div>
          <span>Intended audience</span>
          <strong>{audiencePreviewLoading ? "Checking" : serverAudienceCount}</strong>
        </div>
      </section>

      <div className="communications-grid">
        <section className="panel">
          <TaskDisclosure
            actionLabel="Compose"
            defaultOpen
            title="Prepare a review copy"
            description="Draft the message and send one review copy before member broadcast delivery is enabled."
          >
          <form className="stacked-form" onSubmit={queueReview}>
            <label>
              Intended audience
              <select value={draft.segment} onChange={(event) => setDraft((current) => ({ ...current, segment: event.target.value }))}>
                <option value="test">Review recipient only</option>
                <option value="active_members">Active members</option>
                <option value="pending_review">Pending review</option>
                <option value="waiver_due">Waiver due</option>
              </select>
            </label>
            <label>
              Chapter
              <select
                value={draft.chapterId}
                onChange={(event) => setDraft((current) => ({ ...current, chapterId: event.target.value }))}
                disabled={!chaptersEnabled || activeChapters.length === 0}
              >
                <option value="">All chapters</option>
                {activeChapters.map((chapter) => (
                  <option key={chapter.chapterId} value={chapter.chapterId}>
                    {chapter.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Review recipient
              <input value={draft.recipientEmail} onChange={(event) => setDraft((current) => ({ ...current, recipientEmail: event.target.value }))} placeholder="owner@example.com" />
            </label>
            <label>
              Subject
              <input value={draft.subject} onChange={(event) => setDraft((current) => ({ ...current, subject: event.target.value }))} />
            </label>
            <label>
              Message
              <textarea rows={8} value={draft.body} onChange={(event) => setDraft((current) => ({ ...current, body: event.target.value }))} placeholder="Write the message members should receive." />
            </label>
            <div className="communications-compose-actions">
              <button type="submit" disabled={queueing || !draft.recipientEmail || !draft.subject || !draft.body}>
                {queueing ? "Queueing..." : "Queue review copy"}
              </button>
              <button
                type="button"
                className="secondary-button"
                disabled={sendingBroadcast || draft.segment === "test" || !draft.subject || !draft.body || audiencePreviewLoading || (audiencePreview?.eligibleCount || 0) <= 0}
                onClick={sendBroadcast}
              >
                {sendingBroadcast ? "Sending..." : "Send broadcast"}
              </button>
            </div>
            {queuedMessage && <span className="form-note">{queuedMessage}</span>}
          </form>
          </TaskDisclosure>
        </section>

        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Audience review</h2>
              <p>The copy the owner reviews before any member broadcast workflow opens.</p>
            </div>
          </div>
          <article className="message-preview">
            <strong>{draft.subject || "Subject"}</strong>
            <span>
              To: {segmentLabel(draft.segment)}
              {draft.chapterId ? ` / ${chapterNameById.get(draft.chapterId) || "selected chapter"}` : ""}
              {" / "}Review recipient: {displayRecipient(draft.recipientEmail) || "not set"}
            </span>
            <p>{draft.body || "Draft a message to preview it here."}</p>
          </article>
          <div className="communications-audience-review">
            <div>
              <span>Eligible</span>
              <strong>{audiencePreviewLoading ? "..." : serverAudienceCount}</strong>
            </div>
            <div>
              <span>Skipped</span>
              <strong>{audiencePreviewLoading ? "..." : skippedAudienceCount}</strong>
            </div>
            {skippedAudienceCount > 0 && (
              <small>
                {Object.entries(audiencePreview?.skippedByReason || {})
                  .map(([reason, count]) => `${count} ${ownerNotificationLabel(reason)}`)
                  .join(" / ")}
              </small>
            )}
            {audiencePreview?.recipients?.length > 0 && (
              <ul>
                {audiencePreview.recipients.slice(0, 5).map((recipient) => (
                  <li key={`${recipient.email || recipient.name}`}>
                    <span>{recipient.name}</span>
                    {recipient.email && <small>{recipient.email}</small>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section className="panel communications-history">
          <div className="panel-header">
            <div>
              <h2>Recent message activity</h2>
              <p>Review copies and delivery attempts tied to Communications.</p>
            </div>
          </div>
          {chaptersEnabled && activeChapters.length > 0 && (
            <div className="communications-toolbar">
              <label>
                Chapter
                <select value={chapterFilter} onChange={(event) => setChapterFilter(event.target.value)}>
                  <option value="">All chapters</option>
                  {activeChapters.map((chapter) => (
                    <option key={chapter.chapterId} value={chapter.chapterId}>
                      {chapter.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
          <div className="table-like compact-table">
            <div className="table-row table-head">
              <span>Subject</span>
              <span>Chapter</span>
              <span>Status</span>
              <span>Recipient</span>
              <span>Updated</span>
            </div>
            {filteredNotifications.slice(0, 12).map((notification) => (
              <div className="table-row" key={notification.notificationId}>
                <span>{displayMessageText(notification.subject || ownerNotificationLabel(notification.type))}</span>
                <span>
                  {notificationChapterName(notification, chapterNameById) ? (
                    <span className="chapter-context-badge">{notificationChapterName(notification, chapterNameById)}</span>
                  ) : "All"}
                </span>
                <span>
                  <span className="communications-activity-status">{activityStatusLabel(notification)}</span>
                  <small className="communications-delivery-history">{deliveryAttemptLabel(notification)}</small>
                  {deliveryAttemptDetail(notification) && (
                    <small className="communications-delivery-history is-muted">{deliveryAttemptDetail(notification)}</small>
                  )}
                </span>
                <span>{displayRecipient(notification.recipientEmail)}</span>
                <span>{formatDateTime(notification.updatedAt || notification.createdAt)}</span>
              </div>
            ))}
          </div>
          {filteredNotifications.length === 0 && (
            <div className="empty-state">
              <strong>No message activity yet.</strong>
              <span>Queue a review message here, then use Notifications when it needs review or delivery action.</span>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
